import { useEffect, useState } from 'react';
import { Badge, Group, Stack, Table, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { ShipmentListItem, ShipmentStatus } from '@expeditor/shared';
import { shipmentsApi } from '../../shared/api/shipments';
import { useRolePrefix } from '../../shared/auth/useRolePrefix';

const COLOR: Record<ShipmentStatus, string> = {
  PROJECT: 'grape',
  CREATED: 'gray',
  PICKED_UP: 'blue',
  IN_TRANSIT: 'orange',
  ARRIVED: 'cyan',
  DELIVERED: 'green',
  CLOSED: 'teal',
  CANCELLED: 'red',
};
const LABEL: Record<ShipmentStatus, string> = {
  PROJECT: 'Проект',
  CREATED: 'Создана',
  PICKED_UP: 'Принят',
  IN_TRANSIT: 'В пути',
  ARRIVED: 'Прибыл',
  DELIVERED: 'Выдан',
  CLOSED: 'Закрыта',
  CANCELLED: 'Отменена',
};

/** Список перевозок. Клиент видит свои, менеджер — все. */
export function ShipmentsListPage({ title }: { title: string }) {
  const prefix = useRolePrefix();
  const [items, setItems] = useState<ShipmentListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void shipmentsApi
      .list()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, []);

  return (
    <Stack>
      <Title order={2}>{title}</Title>
      {error && <Text c="red">{error}</Text>}
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Договор</Table.Th>
            <Table.Th>Поручение</Table.Th>
            <Table.Th>Клиент</Table.Th>
            <Table.Th>Груз</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th>В пути с</Table.Th>
            <Table.Th>Доставлен</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((s) => (
            <Table.Tr key={s.id}>
              <Table.Td>
                <Text fw={500} c="blue" component={Link} to={`${prefix}/shipments/${s.id}`}>
                  {s.contractNumber ?? '(проект)'}
                </Text>
              </Table.Td>
              <Table.Td>{s.orderNumber}</Table.Td>
              <Table.Td>{s.clientDisplayName ?? '—'}</Table.Td>
              <Table.Td>{s.cargoName}</Table.Td>
              <Table.Td>
                <Badge color={COLOR[s.status]}>{LABEL[s.status]}</Badge>
              </Table.Td>
              <Table.Td>
                {s.startedAt ? new Date(s.startedAt).toLocaleDateString('ru-RU') : '—'}
              </Table.Td>
              <Table.Td>
                {s.finishedAt ? new Date(s.finishedAt).toLocaleDateString('ru-RU') : '—'}
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Group justify="center" py="md">
                  <Text c="dimmed">Перевозок пока нет</Text>
                </Group>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
